import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "@themesberg/react-bootstrap";
import { useCookies } from "react-cookie";
import {
  getLotto535Results,
  saveLotto535Result,
} from "../../../controller/api/gameResult";
import { calculateLotto535, previewLotto535Reward } from "../../../controller/api/result";
import { calculatePage, convertToVietnamTime } from "../../../helper/helper";
import Pagination from "../../../components/Pagination";
import RewardCalcAutoToggle from "../../../components/RewardCalcAutoToggle";
import Lotto535RewardTable from "../../../components/Lotto535RewardTable";
import RewardCalcStatusCell from "../../../components/RewardCalcStatusCell";

const PRIZES = [
  ["jackpot", "Jackpot"], ["firstPrize", "Giải Nhất"],
  ["secondPrize", "Giải Nhì"], ["thirdPrize", "Giải Ba"],
  ["fourthPrize", "Giải Tư"], ["fifthPrize", "Giải Năm"],
  ["sixthPrize", "Giải Khuyến khích"],
];

const formatMoney = (value) => String(value ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const resultNumbers = (item) => {
  if (!Array.isArray(item?.result) || !item.result.length) return [];
  return item.result.length === 1 ? String(item.result[0]).trim().split(/[\s,|;-]+/) : item.result;
};
const hasResult = (item) => resultNumbers(item).length === 6;

function ResultBalls({ item }) {
  const numbers = resultNumbers(item);
  if (numbers.length !== 6) return <span className="text-muted">Chưa có kết quả</span>;
  return <div className="d-flex align-items-center flex-wrap" style={{ gap: 5 }}>
    {numbers.slice(0, 5).map((number, index) =>
      <Badge key={`${number}-${index}`} bg="danger" className="rounded-circle py-2 px-2">{String(number).padStart(2, "0")}</Badge>
    )}
    <b className="mx-1">+</b>
    <Badge bg="warning" text="dark" className="rounded-circle py-2 px-2">{String(numbers[5]).padStart(2, "0")}</Badge>
  </div>;
}

function PrizeDetails({ analytic }) {
  const complete = analytic && PRIZES.every(([key]) =>
    Object.prototype.hasOwnProperty.call(analytic, `${key}Count`) &&
    Object.prototype.hasOwnProperty.call(analytic, `${key}Amount`)
  );
  if (!complete) return <Alert variant="warning" className="mb-0">Thiếu chi tiết số lượng trúng hoặc mức trả của 7 hạng giải. Không được dùng dữ liệu này để tính thưởng.</Alert>;
  return <Table bordered size="sm" className="mb-0">
    <thead><tr><th>Hạng giải</th><th>Số lượng trúng</th><th>Mức trả / bộ</th></tr></thead>
    <tbody>{PRIZES.map(([key, label]) => <tr key={key}>
      <td>{label}</td><td>{formatMoney(analytic[`${key}Count`])}</td>
      <td>{formatMoney(analytic[`${key}Amount`])}đ</td>
    </tr>)}</tbody>
  </Table>;
}

function ResultEditor({ item, show, onHide, onSaved, token }) {
  const numbers = resultNumbers(item);
  const [mainText, setMainText] = useState("");
  const [specialNumber, setSpecialNumber] = useState("");
  const [analytic, setAnalytic] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setMainText(numbers.slice(0, 5).join(" "));
    setSpecialNumber(numbers[5] || "");
    setAnalytic(item?.analytic || {});
    setError("");
  }, [show, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const mainNumbers = mainText.trim().split(/[\s,|;-]+/).filter(Boolean).map(Number);
    const analyticComplete = PRIZES.every(([key]) =>
      analytic[`${key}Count`] !== "" && analytic[`${key}Count`] !== undefined &&
      analytic[`${key}Amount`] !== "" && analytic[`${key}Amount`] !== undefined
    );
    if (mainNumbers.length !== 5 || new Set(mainNumbers).size !== 5 || !analyticComplete) {
      setError("Vui lòng nhập đủ 5 số chính khác nhau và đầy đủ 7 hạng giải");
      return;
    }
    setSaving(true); setError("");
    try {
      await saveLotto535Result(token, {
        periodId: item.gamePeriodId,
        mainNumbers,
        specialNumber: Number(specialNumber),
        analytic: Object.fromEntries(PRIZES.flatMap(([key]) => [
          [`${key}Count`, Number(analytic[`${key}Count`] ?? 0)],
          [`${key}Amount`, String(analytic[`${key}Amount`] ?? "0").replace(/\D/g, "")],
        ])),
      }, hasResult(item));
      onSaved(); onHide();
    } catch (err) {
      setError(err?.response?.data?.message || "Không lưu được kết quả");
    } finally { setSaving(false); }
  };

  return <Modal show={show} onHide={onHide} size="lg" centered scrollable>
    <Modal.Header closeButton><Modal.Title>Kết quả Lotto 5/35 — kỳ {item?.gamePeriod?.name}</Modal.Title></Modal.Header>
    <Modal.Body>
      {error && <Alert variant="danger">{error}</Alert>}
      <Alert variant="info">Kết quả hợp lệ được lưu trực tiếp và dùng cho luồng tính thưởng của kỳ này.</Alert>
      <Row className="g-3 mb-3">
        <Col md={8}><Form.Label>5 số chính (01–35)</Form.Label><Form.Control value={mainText} onChange={(e) => setMainText(e.target.value)} placeholder="01 02 03 04 05" /></Col>
        <Col md={4}><Form.Label>Số đặc biệt (01–12)</Form.Label><Form.Control type="number" min={1} max={12} value={specialNumber} onChange={(e) => setSpecialNumber(e.target.value)} /></Col>
      </Row>
      <Table responsive size="sm"><thead><tr><th>Hạng giải</th><th>Số lượng trúng</th><th>Mức trả / bộ</th></tr></thead>
        <tbody>{PRIZES.map(([key, label]) => <tr key={key}><td>{label}</td>
          <td><Form.Control required type="number" min={0} value={analytic[`${key}Count`] ?? ""} onChange={(e) => setAnalytic({ ...analytic, [`${key}Count`]: e.target.value })} /></td>
          <td><Form.Control required inputMode="numeric" value={analytic[`${key}Amount`] ?? ""} onChange={(e) => setAnalytic({ ...analytic, [`${key}Amount`]: e.target.value.replace(/\D/g, "") })} /></td>
        </tr>)}</tbody>
      </Table>
    </Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" onClick={onHide}>Đóng</Button><Button disabled={saving} onClick={save}>{saving ? "Đang lưu..." : "Lưu kết quả"}</Button></Modal.Footer>
  </Modal>;
}

export default function Lotto535Result() {
  const [cookies] = useCookies(["lottmekong"]);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ list: [], total: 0, totalPage: 0 });
  const [expanded, setExpanded] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadResults = useCallback(async () => {
    const response = await getLotto535Results(cookies.lottmekong, page);
    setData({ list: response.data || [], total: response.total || 0, totalPage: calculatePage(response.total || 0) });
  }, [cookies.lottmekong, page]);
  useEffect(() => { document.title = "Kết quả Lotto 5/35"; loadResults(); }, [loadResults]);

  const run = async (key, action, successText) => {
    setBusy(key); setNotice(null);
    try {
      const result = await action();
      setNotice({ type: "success", text: `${successText}${result?.totalTicket !== undefined ? `: ${result.totalTicket} vé` : ""}` });
      await loadResults();
    } catch (err) {
      setNotice({ type: "danger", text: err?.response?.data?.message || "Thao tác thất bại" });
    } finally { setBusy(""); }
  };

  const previewReward = async (item) => {
    const key = `preview-${item.gamePeriodId}`;
    setBusy(key); setNotice(null);
    try {
      const result = await previewLotto535Reward(cookies.lottmekong, 50, item.gamePeriodId);
      setPreview({ ...result, periodName: item.gamePeriod?.name, periodId: item.gamePeriodId });
    } catch (err) {
      setNotice({ type: "danger", text: err?.response?.data?.message || "Không xem trước được kết quả dò thưởng" });
    } finally { setBusy(""); }
  };

  return <>
    <RewardCalcAutoToggle gameId="lotto535" gameName="Lotto 5/35" />
    {notice && <Alert className="mt-3" variant={notice.type}>{notice.text}</Alert>}
    <Card border="light" className="shadow-sm mt-4">
      <Card.Header><h5 className="mb-1">Kết quả Lotto 5/35 ({data.total})</h5><small className="text-muted">Kết quả crawler hoặc Admin nhập được dùng trực tiếp; tự động tính/trả theo cấu hình chung.</small></Card.Header>
      <Table responsive className="align-items-center table-flush mb-0"><thead><tr><th>Kỳ quay</th><th>Thời gian quay (VN)</th><th>Kết quả</th><th>Vé / thưởng</th><th>Thao tác</th><th>Trạng thái</th></tr></thead>
        <tbody>{data.list.map((item) => {
          const resultReady = hasResult(item);
          return <React.Fragment key={item.gamePeriodId}><tr>
            <td><b>#{item.gamePeriod?.name}</b><br/><small>{item.gamePeriodId}</small></td>
            <td>{item.gamePeriod?.resultDate}<br/><span className="text-info">{convertToVietnamTime(item.gamePeriod?.dialDate)}</span></td>
            <td><ResultBalls item={item}/></td><td>{item.totalRewaredTickets || 0} trúng / {item.totalUnRewardTickets || 0} không trúng</td>
            <td><div className="d-flex flex-wrap" style={{ gap: 5 }}>
              {resultReady && <Button size="sm" variant="outline-info" onClick={() => setExpanded(expanded === item.gamePeriodId ? "" : item.gamePeriodId)}>Xem kết quả</Button>}
              <Button size="sm" variant="outline-primary" onClick={() => setEditing(item)}>{resultReady ? "Sửa" : "Nhập kết quả"}</Button>
              {resultReady && <Button size="sm" variant="outline-secondary" disabled={!!busy} onClick={() => previewReward(item)}>{busy === `preview-${item.gamePeriodId}` ? <Spinner size="sm" animation="border"/> : "Xem trước"}</Button>}
              {resultReady && <Button size="sm" variant="warning" disabled={!!busy} onClick={() => window.confirm("Tính thưởng cho tối đa 50 vé đã in của kỳ này?") && run(`calc-${item.gamePeriodId}`, () => calculateLotto535(cookies.lottmekong, 50, item.gamePeriodId), "Tính thưởng hoàn tất")}>Tính thưởng</Button>}
            </div></td>
            <RewardCalcStatusCell
              status={item.status}
              totalTickets={item.totalTickets}
              totalRewaredTickets={item.totalRewaredTickets}
              totalUnRewardTickets={item.totalUnRewardTickets}
            />
          </tr>{expanded === item.gamePeriodId && <tr><td colSpan="6"><PrizeDetails analytic={item.analytic}/></td></tr>}</React.Fragment>;
        })}{!data.list.length && <tr><td colSpan="6" className="text-center text-muted">Chưa có kỳ đã quay</td></tr>}</tbody>
      </Table>
      <Card.Footer><Row className="align-items-center"><Col><Pagination totalPage={data.totalPage} currentPage={page} onPageChange={setPage}/></Col></Row></Card.Footer>
    </Card>
    {editing && <ResultEditor item={editing} show={!!editing} token={cookies.lottmekong} onHide={() => setEditing(null)} onSaved={loadResults}/>} 
    <Modal show={!!preview} onHide={() => setPreview(null)} size="xl" centered scrollable>
      <Modal.Header closeButton><Modal.Title>Xem trước dò thưởng — kỳ {preview?.periodName}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Alert variant="info">Chế độ xem trước chỉ đọc: không đổi vé/kết quả, không tạo giao dịch thưởng và không cộng ví.</Alert>
        <p><b>Kỳ:</b> {preview?.periodName} &nbsp; <b>Số vé:</b> {preview?.ticketCount || 0}</p>
        {(preview?.tickets || []).map((ticket) => <Card key={ticket.ticketId} className="mb-3">
          <Card.Header><b>Vé {ticket.ticketId}</b><span className="float-end">Tổng dự kiến: <b>{formatMoney(ticket.totalGrossAmount)}đ</b></span></Card.Header>
          <Card.Body className="p-0"><Lotto535RewardTable numberRows={(ticket.rows || []).map((row) => ({
            ...row,
            id: row.ticketRowId,
            rewardBreakdowns: row.breakdowns || [],
          }))}/></Card.Body>
        </Card>)}
        {!preview?.ticketCount && <Alert variant="warning">Không có vé nào thuộc kỳ này để xem trước.</Alert>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={() => setPreview(null)}>Đóng</Button></Modal.Footer>
    </Modal>
  </>;
}
